import { emptyViewVariants } from '@components'

import { ModuleLayout } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Row } from 'react-table'

import { BillStatus } from '@features/bills/enums/billStatus'
import {
  ContactBalanceEmpty,
  ContactBalanceTable,
  ContactBalanceTableActions,
  ContactBalanceTableRow,
  ContactBalanceType,
  useContactBalanceListFilters,
} from '@features/contactBalance'

import { PeriodMode } from '../../../../enums/periodMode'
import { SortDirection } from '../../../../enums/sortDirection'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { getQueryParamsString } from '../../../../utils/getQueryParamsString'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { BillsSortProperty } from '../../../bills/query-api'
import { BillsListFilters } from '../../../bills/routes/BillsList/legacy'
import { useCreditors } from './hooks/useCreditors'

export const CreditorsList = (): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { creditors, isLoading, pagination } = useCreditors()
  const [{ searchQuery }] = useContactBalanceListFilters()

  const { url: billsUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getMainRoute())

  const showEmptyView = useMemo(
    () => !isLoading && !searchQuery && creditors.length === 0,
    [isLoading, searchQuery, creditors.length],
  )

  const onTableRowClick = useCallback(
    (row: Row<ContactBalanceTableRow>) => {
      const { id: contactId, contactName } = row.original

      const invoicesQueryParams: BillsListFilters = {
        contactId,
        contactName,
        period: PeriodMode.All,
        sortProperty: BillsSortProperty.DueDate,
        sortDirection: SortDirection.Asc,
        status: BillStatus.Unpaid,
      }

      const queryParamsString = getQueryParamsString(invoicesQueryParams)
      navigate(`${billsUrl}?${queryParamsString}`)
    },
    [billsUrl, navigate],
  )

  return (
    <ModuleLayout title={t('creditors')}>
      <ContactBalanceTableActions isLoading={isLoading} title={t('creditors')} total={pagination?.total} />
      {showEmptyView && billsUrl ? (
        <ContactBalanceEmpty {...emptyViewVariants.creditors} path={billsUrl} />
      ) : (
        <ContactBalanceTable
          data={creditors}
          isLoading={isLoading || !billsUrl}
          contactBalanceType={ContactBalanceType.Creditor}
          onRowClick={onTableRowClick}
        />
      )}
    </ModuleLayout>
  )
}
